import styled from '@emotion/styled'
import React, { useEffect } from 'react'
import { Platform } from '@/firebase/firestore/Platform'
import { Sex } from '@/firebase/firestore/Sex'
import { VoiceChat } from '@/firebase/firestore/VoiceChats'
import Image from 'next/image'
import fitty from 'fitty'
import { useLocale } from '@/hooks/useLocale'

type Props = {
  targetId: string
  templateColor: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7
  previewImage: string
  twitterId: string
  discordId: string
  name: string
  sex: Sex
  platform: Array<Platform>
  voiceChat: Array<VoiceChat>
  voiceChatOption: string
  playStyleRange: number
  battlePositionRange: number
  fontFamily: string
  fontColor: string
  playTimeWeekDayBegin: string
  playTimeWeekDayEnd: string
  playTimeWeekEndBegin: string
  playTimeWeekEndEnd: string
  playTime: string
  startTiming: string
  experience: string
  hitokoto: string
  classes: number[]
}

//こさやんさんテンプレート2
export const BlueProtocol2: React.FC<Props> = ({
  previewImage,
  twitterId,
  discordId,
  name,
  sex,
  fontFamily,
  fontColor,
  playStyleRange,
  battlePositionRange,
  platform,
  templateColor,
  voiceChat,
  voiceChatOption,
  playTimeWeekDayBegin,
  playTimeWeekDayEnd,
  playTimeWeekEndBegin,
  playTimeWeekEndEnd,
  playTime,
  hitokoto,
  startTiming,
  experience,
  classes,
  targetId,
}) => {
  const { t } = useLocale()
  useEffect(() => {
    fitty('#name', {
      maxSize: 24,
      minSize: 12,
    })
    fitty('#twitterId', {
      maxSize: 14,
      minSize: 8,
    })
    fitty('#discordId', {
      maxSize: 14,
      minSize: 8,
    })
    fitty('#playStyle', {
      maxSize: 16,
      minSize: 12,
    })
    fitty('#playTime', {
      maxSize: 16,
      minSize: 11,
    })
    fitty('#voiceChat', {
      maxSize: 15,
      minSize: 8,
    })
    fitty('#experience', {
      maxSize: 24,
      minSize: 12,
    })
    fitty('#hitokoto', {
      maxSize: 28,
      minSize: 14,
    })
  }, [])
  return (
    <Container id={targetId} fontFamily={fontFamily} fontColor={fontColor}>
      <Image
        width="1050"
        height="745"
        priority={true}
        unoptimized={true}
        src={`/static/images/profiles/blueProtocol2/${templateColor}.png`}
        alt={`BLUE PROTOCOL自己紹介カード ${templateColor}`}
      />
      <PreviewImage src={previewImage} />
      <Name>
        <span id="name">{name}</span>
      </Name>
      <TwitterId>
        <span id="twitterId">{twitterId}</span>
      </TwitterId>
      <DiscordId>
        <span id="discordId">{discordId}</span>
      </DiscordId>
      {voiceChatOption === 'ok' && <VCCheck0 />}
      {voiceChatOption === 'ng' && <VCCheck1 />}
      {voiceChatOption === 'listenOnly' && <VCCheck2 />}
      {voiceChatOption === 'depends' && <VCCheck3 />}
      {classes.includes(0) && <Class0 />}
      {classes.includes(1) && <Class1 />}
      {classes.includes(2) && <Class2 />}
      {classes.includes(3) && <Class3 />}
      {classes.includes(4) && <Class4 />}
      <VCText color={fontColor}>
        <span id="voiceChat">
          {voiceChat.includes(0) && ' Skype '}
          {voiceChat.includes(1) && ' LINE '}
          {voiceChat.includes(2) && ' プレステ '}
          {voiceChat.includes(3) && ' Discord '}
          {voiceChat.includes(4) && ' 聞き専 '}
          {voiceChat.includes(5) && ' Switch '}
        </span>
      </VCText>
      <PlayStyleRange value={playStyleRange} />
      <BattlePositionRange value={battlePositionRange} />
      <Experience>
        <span id={'experience'}>{experience}</span>
      </Experience>
      <Hitokoto>
        <span id={'hitokoto'}>{hitokoto}</span>
      </Hitokoto>
    </Container>
  )
}

const Container = styled.div<{ fontFamily: string; fontColor: string }>`
  position: relative;
  width: 100%;
  line-height: 0;
  * {
    line-height: 1;
    font-family: ${({ fontFamily }) => fontFamily};
    color: ${({ fontColor }) => fontColor};
    border-color: ${({ fontColor }) => fontColor};
  }
`

const PreviewImage = styled.img`
  position: absolute;
  top: 4%;
  left: 82%;
  width: 72px;
  height: 88px;
  object-fit: cover;
`

const Name = styled.div`
  position: absolute;
  top: 9%;
  left: 49%;
  width: 32%;
  height: 14%;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    letter-spacing: -2px;
    font-weight: bold;
  }
`

const TwitterId = styled.div`
  position: absolute;
  top: 21.5%;
  left: 52%;
  text-align: center;
  width: 28%;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    font-weight: bold;
  }
`

const DiscordId = styled.div`
  position: absolute;
  top: 28.5%;
  left: 52%;
  text-align: center;
  width: 28%;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    font-weight: bold;
  }
`

const PlayStyle = styled.div`
  position: absolute;
  top: 49%;
  left: 43%;
  width: 54%;
  height: 10%;
  display: flex;
  justify-content: center;

  span {
    font-weight: bold;
    line-height: 0.9;
  }
`

const VCBase = styled.div`
  position: absolute;
  top: 48%;
  left: 51.5%;
  width: 14px;
  height: 7px;
  border-left: 3px solid;
  border-bottom: 3px solid;
  transform: rotate(-45deg);
`

const VCCheck0 = styled(VCBase)``
const VCCheck1 = styled(VCBase)`
  top: 51%;
  left: 51.5%;
`
const VCCheck2 = styled(VCBase)`
  top: 48%;
  left: 59%;
`
const VCCheck3 = styled(VCBase)`
  top: 51%;
  left: 59%;
`

const PlayStyleRange = styled.div<{ value: number }>`
  position: absolute;
  top: 37%;
  left: ${({ value }) => 50.5 + (85 - 50.5) * (value / 8)}%;
  border-style: solid;
  border-width: 2px;
  border-radius: 50%;
  width: 12px;
  height: 12px;
`

const BattlePositionRange = styled.div<{ value: number }>`
  position: absolute;
  top: 42%;
  left: ${({ value }) => 53.5 + (85 - 53.5) * (value / 7)}%;
  border-style: solid;
  border-width: 2px;
  border-radius: 50%;
  width: 12px;
  height: 12px;
`

const ClassBase = styled.div`
  position: absolute;
  top: 29.5%;
  left: 16.4%;
  border-style: solid;
  border-width: 2.5px;
  border-radius: 50%;
  width: 34px;
  height: 34px;
`

const Class0 = styled(ClassBase)``
const Class1 = styled(ClassBase)`
  top: 40.5%;
  left: 26.6%;
`
const Class2 = styled(ClassBase)`
  top: 55.5%;
  left: 23.5%;
`
const Class3 = styled(ClassBase)`
  top: 55.5%;
  left: 9.8%;
`
const Class4 = styled(ClassBase)`
  top: 40.5%;
  left: 6%;
`

const VCText = styled.div`
  position: absolute;
  top: 52%;
  left: 32%;
  width: 54%;
  height: 14%;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    letter-spacing: -1px;
    font-weight: bold;
  }
`

const Experience = styled.div`
  position: absolute;
  top: 38%;
  left: 76.5%;
  height: 26%;
  width: 22%;
  word-break: break-all;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    font-weight: bold;
    line-height: 1.4;
  }
`

const Hitokoto = styled.div`
  position: absolute;
  top: 64%;
  left: 44%;
  height: 26%;
  width: 52%;
  word-break: break-all;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    font-weight: bold;
    line-height: 1.4;
  }
`
